import { Progress } from 'antd'
import {
  MetricLabel,
  MetricValue,
} from 'features/inbox/components/shared/styledElements'
import {
  MeasurementValue,
  MetricDefinition,
} from 'features/inbox/config/inspectionMetrics'
import { EnrichedGeckoInspection } from 'features/inbox/types/types'
import React, { memo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const ProgressBarContainer = styled.div`
  margin-top: 0.5rem;
`

interface InspectionMetricRendererProps {
  metric:
    | MetricDefinition<MeasurementValue>
    | MetricDefinition<string>
    | MetricDefinition<number>
    | MetricDefinition<number | undefined>
  inspection: EnrichedGeckoInspection
}

/**
 * Renders a single inspection metric based on its type
 * Supports 'value' (simple text display) and 'progress' (progress bar) components
 */
const InspectionMetricRendererComponent: React.FC<
  InspectionMetricRendererProps
> = ({ metric, inspection }) => {
  const value = metric.getValue(inspection)
  // Type assertion is safe here because format and getValue share the same type parameter T
  const formattedValue =
    // @ts-expect-error - TypeScript can't infer that getValue's return type matches format's parameter type in union types
    metric.format && value ? metric.format(value, inspection) : String(value)

  if (metric.component === 'progress') {
    const rawProgressValue =
      typeof value === 'string' ? parseFloat(value) : Number(value)
    const progressValue = Math.min(rawProgressValue, 100)

    return (
      <MetricItem key={metric.key}>
        <MetricLabel>{metric.label}</MetricLabel>
        <MetricValue>{formattedValue}</MetricValue>
        <ProgressBarContainer>
          <Progress
            percent={progressValue}
            size='default'
            showInfo={false}
            strokeColor={CantileverTheme.colors.alternate['200']}
            trailColor={CantileverTheme.colors.grayscale['600']}
          />
        </ProgressBarContainer>
      </MetricItem>
    )
  }

  // Default to 'value' component
  return (
    <MetricItem key={metric.key}>
      <MetricLabel>{metric.label}</MetricLabel>
      <MetricValue>{formattedValue}</MetricValue>
    </MetricItem>
  )
}

export const InspectionMetricRenderer = memo(InspectionMetricRendererComponent)
